import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import {
	FEED_TABS,
	FeedCompanyJumpEntry,
	FeedCounts,
	FeedLeadScoreFilter,
	FeedPageResponse,
	FeedRunCard,
	FeedSignalScoreFilter,
	FeedSort,
	FeedTab,
} from "@signalist/shared-types";

import { RootState } from "@/redux";

import { feedCardKey } from "@/pages/Private/pages/Feed/utils/feedCardKey";

import { FeedFilterArgs, feedApi } from "./feed.api";

import type { IFilterWithValues } from "@/pages/Private/pages/List/components/FilterModal";

/** One selected tracker list — the RTS-style `{id, title}` URL shape. */
export interface FeedSelectedList {
	id: number;
	title: string;
}

/**
 * Pseudo-tab embedding the tracker table (D5). Page-only — never sent to the
 * feed endpoints, whose `tab` vocabulary is the four run-card buckets.
 */
export const FEED_TRACKED_TAB = "tracked" as const;

export type FeedPageTab = FeedTab | typeof FEED_TRACKED_TAB;

/**
 * Which view the docked panel opens on (P6 D6): "chat" when the selection came
 * from *Write manually* / *review now*, "lead-panel" for plain card clicks.
 */
export type FeedPanelContextView = "lead-panel" | "chat";

export interface FeedState {
	/** Selected tracker lists; empty until the page hydrates from URL / defaults. */
	selectedLists: FeedSelectedList[];
	tab: FeedPageTab;
	page: number;
	sort: FeedSort;
	search: string;
	/** Lead-score filter (list-page PriorityFilterStatus values); empty = all leads. */
	priorityFilterStatus: FeedLeadScoreFilter[];
	/** Signal-score filter (list-page SignalFilterStatus values); empty = all signals. */
	signalFilterStatus: FeedSignalScoreFilter[];
	/** Funnel-modal filter (list-page FilterModal) — single-list selections only. */
	filter: IFilterWithValues | null;
	/** Run id of the selected card (docked panel target — consumed by P5). */
	selectedCardId: number | null;
	/**
	 * Card identity a jump-to-company pick is waiting to land on (D3). Set together with the
	 * target `page`, consumed by `useFeedJumpToCompany` once that page's cards have settled —
	 * the cross-page jump has to survive the refetch gap, and a same-page jump needs *some*
	 * state change to react to (setting the page it is already on is a no-op).
	 */
	jumpTargetCardId: number | null;
	/**
	 * The view the pending jump wants its card's panel opened on — "chat" for a queue-pill
	 * review (P6 D6), null for a plain jump-dropdown pick. Applied on landing, because
	 * landing selects the card and a selection resets the view to the lead panel; dispatching
	 * "chat" at click time would be undone a refetch later.
	 */
	jumpTargetContextView: FeedPanelContextView | null;
	/** The docked panel's initial view for the current selection (P6 D6). */
	panelContextView: FeedPanelContextView;
	/**
	 * Explicit person switches per card (runId → listPersonId, D5 — the feed is
	 * list_person-driven). No entry means "the card's first person"; pruned to the
	 * current page on every fetch.
	 */
	selectedPersonByRunId: Record<number, number>;
	cards: FeedRunCard[];
	meta: FeedPageResponse["meta"] | null;
	counts: FeedCounts | null;
	companies: FeedCompanyJumpEntry[];
}

const initialState: FeedState = {
	selectedLists: [],
	tab: "new",
	page: 1,
	sort: "strength",
	search: "",
	priorityFilterStatus: [],
	signalFilterStatus: [],
	filter: null,
	selectedCardId: null,
	jumpTargetCardId: null,
	jumpTargetContextView: null,
	panelContextView: "lead-panel",
	selectedPersonByRunId: {},
	cards: [],
	meta: null,
	counts: null,
	companies: [],
};

const isValidTab = (tab: unknown): tab is FeedPageTab =>
	tab === FEED_TRACKED_TAB || FEED_TABS.includes(tab as FeedTab);

const isValidPage = (page: unknown): page is number =>
	typeof page === "number" && Number.isInteger(page) && page >= 1;

/** Shared by the reducer and the ignore matcher — the fresh counts win (D3). */
const applyCounts = (state: FeedState, counts: FeedCounts): void => {
	state.counts = counts;
};

/** Order-independent equality of two id/status lists. */
const sameSet = (a: (number | string)[], b: (number | string)[]): boolean => {
	if (a.length !== b.length) {
		return false;
	}

	const seen = new Set(a);

	return b.every((value) => seen.has(value));
};

/**
 * True when a `getFeedCounts` response was produced by the Feed page's *current* query —
 * the selected lists + active filters — rather than the sidebar badge's all-tracker-lists
 * query, which hits the same endpoint. Only the page's own counts may overwrite
 * `state.counts`; otherwise the badge's all-lists response clobbers the list-scoped tab
 * counts (PREC-3584). Comparing the full filter args also drops stale in-flight responses
 * after the selection changes.
 */
const argsMatchFeedSelection = (state: FeedState, args: FeedFilterArgs): boolean =>
	sameSet(
		state.selectedLists.map((list) => Number(list.id)),
		args.listIds
	) &&
	state.search === args.search &&
	sameSet(state.priorityFilterStatus, args.priorityFilterStatus) &&
	sameSet(state.signalFilterStatus, args.signalFilterStatus) &&
	JSON.stringify(state.filter) === JSON.stringify(args.filter);

/** Drops the pending jump and the panel intent riding with it — the two are one target. */
const clearJumpTarget = (state: FeedState): void => {
	state.jumpTargetCardId = null;
	state.jumpTargetContextView = null;
};

/** Drops a card and every state keyed to it (selection, person switch, pending jump). */
const removeCard = (state: FeedState, runId: number): void => {
	state.cards = state.cards.filter((card) => card.runId !== runId);
	delete state.selectedPersonByRunId[runId];

	if (state.selectedCardId === runId) {
		state.selectedCardId = null;
		state.panelContextView = "lead-panel";
	}

	if (state.jumpTargetCardId === runId) {
		clearJumpTarget(state);
	}
};

/**
 * Resets to page 1 and drops a pending jump target — for every change that reshuffles the
 * result set (tab, sort, search, filters). The target's page came from the *previous* query,
 * so keeping it would scroll to whatever card inherited that identity's slot.
 */
const resetPaging = (state: FeedState): void => {
	state.page = 1;
	clearJumpTarget(state);
};

export const feedSlice = createSlice({
	name: "feed",
	initialState,
	reducers: {
		/**
		 * Seeds lists/tab/page from the URL in one shot (deep link, D1). Invalid
		 * values fall back to the defaults instead of throwing away the rest.
		 */
		hydrateFeedFromUrl: (
			state,
			{
				payload,
			}: PayloadAction<{ selectedLists?: FeedSelectedList[]; tab?: unknown; page?: unknown }>
		) => {
			if (payload.selectedLists?.length) {
				state.selectedLists = payload.selectedLists;
			}

			state.tab = isValidTab(payload.tab) ? payload.tab : initialState.tab;
			state.page = isValidPage(payload.page) ? payload.page : initialState.page;
		},
		setFeedSelectedLists: (state, { payload }: PayloadAction<FeedSelectedList[]>) => {
			state.selectedLists = payload;
			resetPaging(state);
			state.selectedCardId = null;
			state.panelContextView = "lead-panel";
			// The funnel filter is scoped to a single list — a selection change
			// invalidates it (the funnel itself only renders for one list).
			state.filter = null;
		},
		setFeedTab: (state, { payload }: PayloadAction<FeedPageTab>) => {
			state.tab = payload;
			resetPaging(state);
			state.selectedCardId = null;
			state.panelContextView = "lead-panel";
		},
		setFeedPage: (state, { payload }: PayloadAction<number>) => {
			state.page = isValidPage(payload) ? payload : 1;
		},
		setFeedSort: (state, { payload }: PayloadAction<FeedSort>) => {
			state.sort = payload;
			resetPaging(state);
		},
		setFeedSearch: (state, { payload }: PayloadAction<string>) => {
			state.search = payload;
			resetPaging(state);
		},
		setFeedPriorityFilterStatus: (state, { payload }: PayloadAction<FeedLeadScoreFilter[]>) => {
			state.priorityFilterStatus = payload;
			resetPaging(state);
		},
		setFeedSignalFilterStatus: (state, { payload }: PayloadAction<FeedSignalScoreFilter[]>) => {
			state.signalFilterStatus = payload;
			resetPaging(state);
		},
		setFeedFilter: (state, { payload }: PayloadAction<IFilterWithValues | null>) => {
			state.filter = payload && Object.keys(payload).length > 0 ? payload : null;
			resetPaging(state);
		},
		/**
		 * Jump-to-company pick (D3) and queue-pill review (P6 D6): pages to the card and marks it
		 * the pending jump target in one action. Both halves must move together — dispatching only
		 * the page is what made picking a company on the *current* page a silent no-op.
		 *
		 * `contextView` rides along so a review lands on Chat: the panel view can only be set once
		 * the card is selected, which happens when the jump lands.
		 */
		jumpToFeedCompany: (
			state,
			{
				payload,
			}: PayloadAction<{ page: number; cardId: number; contextView?: FeedPanelContextView }>
		) => {
			state.page = isValidPage(payload.page) ? payload.page : 1;
			state.jumpTargetCardId = payload.cardId;
			state.jumpTargetContextView = payload.contextView ?? null;
		},
		/** Consumed (scrolled + selected) or unreachable — either way the target is spent. */
		clearFeedJumpTarget: (state) => {
			clearJumpTarget(state);
		},
		setFeedSelectedCardId: (state, { payload }: PayloadAction<number | null>) => {
			state.selectedCardId = payload;
			// A plain selection opens on the lead panel — the chat flows (Write
			// manually / review now) dispatch setFeedPanelContextView after this.
			state.panelContextView = "lead-panel";
		},
		setFeedPanelContextView: (state, { payload }: PayloadAction<FeedPanelContextView>) => {
			state.panelContextView = payload;
		},
		setFeedSelectedPerson: (
			state,
			{ payload }: PayloadAction<{ runId: number; listPersonId: number }>
		) => {
			state.selectedPersonByRunId[payload.runId] = payload.listPersonId;
		},
		/**
		 * Optimistic strength change (D1) / ignore-signal DQ (D4). Dispatching again
		 * with the previous priority is the undo revert — no refetch, so the cards
		 * keep their order mid-read.
		 */
		updateFeedSignalPriority: (
			state,
			{ payload }: PayloadAction<{ signalId: number; priority: string | null }>
		) => {
			for (const card of state.cards) {
				for (const signal of card.signals) {
					if (signal.signalId === payload.signalId) {
						signal.priority = payload.priority;
					}
				}
			}
		},
		removeFeedCard: (state, { payload }: PayloadAction<number>) => {
			removeCard(state, payload);
		},
		/**
		 * Optimistic drafts-done marker: when a ready job is consumed (review now),
		 * the job leaves the orchestration map — flipping the person's flag keeps the
		 * card button on the "drafts done → open chat" state instead of reverting to
		 * idle until the next feed refetch. The same lead can sit on several cards.
		 */
		markFeedPersonCopywritingDone: (state, { payload }: PayloadAction<number>) => {
			for (const card of state.cards) {
				for (const person of card.people) {
					if (person.listPersonId === payload) {
						person.copywritingCompleted = true;
					}
				}
			}
		},
		applyFeedCounts: (state, { payload }: PayloadAction<FeedCounts>) => {
			applyCounts(state, payload);
		},
	},
	extraReducers: (builder) => {
		builder.addMatcher(feedApi.endpoints.getFeed.matchFulfilled, (state, { payload }) => {
			state.cards = payload.data;
			state.meta = payload.meta;

			// Prune explicit person switches to the card identities of the new page (D5). Keyed by
			// `feedCardKey` (runId, or the runless fallback) — the same identity the cards render on.
			const cardKeys = new Set(payload.data.map((card) => feedCardKey(card)));

			for (const key of Object.keys(state.selectedPersonByRunId)) {
				if (!cardKeys.has(Number(key))) {
					delete state.selectedPersonByRunId[Number(key)];
				}
			}
		});
		// Ignore-this-time (D3): drop the card in place (no refetch — the pager
		// position survives) and apply the fresh counts from the response.
		builder.addMatcher(feedApi.endpoints.ignoreFeedRun.matchFulfilled, (state, { payload }) => {
			if (!payload.data) {
				return;
			}

			removeCard(state, payload.data.runId);
			applyCounts(state, payload.data.counts);
		});
		// Undo (D3): the `Feed` invalidation refetches the page; only counts apply here.
		builder.addMatcher(feedApi.endpoints.undoIgnoreFeedRun.matchFulfilled, (state, { payload }) => {
			if (payload.data) {
				applyCounts(state, payload.data.counts);
			}
		});
		builder.addMatcher(feedApi.endpoints.getFeedCounts.matchFulfilled, (state, action) => {
			// The sidebar badge queries this same endpoint for *all* tracker lists; ignore
			// its response so the badge's all-lists counts don't clobber the Feed page's
			// list-scoped tab counts (PREC-3584).
			if (!argsMatchFeedSelection(state, action.meta.arg.originalArgs)) {
				return;
			}

			state.counts = action.payload.data ?? null;
		});
		// `?? []` — an off-contract response (missing `data` wrapper) must degrade
		// to an empty list, never crash the jump dropdown on `companies.length`.
		builder.addMatcher(feedApi.endpoints.getFeedCompanies.matchFulfilled, (state, { payload }) => {
			state.companies = payload.data ?? [];
		});
	},
});

export const {
	hydrateFeedFromUrl,
	setFeedSelectedLists,
	setFeedTab,
	setFeedPage,
	setFeedSort,
	setFeedSearch,
	setFeedPriorityFilterStatus,
	setFeedSignalFilterStatus,
	setFeedFilter,
	jumpToFeedCompany,
	clearFeedJumpTarget,
	setFeedSelectedCardId,
	setFeedPanelContextView,
	setFeedSelectedPerson,
	updateFeedSignalPriority,
	removeFeedCard,
	markFeedPersonCopywritingDone,
	applyFeedCounts,
} = feedSlice.actions;

export const feedReducer = feedSlice.reducer;

export const feedSelector = (state: RootState): FeedState => state[feedSlice.name];
